import React, { useEffect } from "react";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import NotoSansKannada from "./NotoSansKannada";
import helperServices from "../../../shared/helper/helper";

function PrintSurgeryConsent(props) {
  const { patientInfo, regDate } = props;

  const name =
    patientInfo?.fullName ||
    [patientInfo?.fname, patientInfo?.mname, patientInfo?.lname]
      .filter(Boolean)
      .join(" ") ||
    "—";

  useEffect(() => {
    props.parentCallBack();
    printConsent();
  }, []);

  const buildDocDefinition = () => {
    const field = (val) => val || "—";

    // ---------------------------------------------------------
    // PATIENT INFORMATION TABLE
    // ---------------------------------------------------------
    const patientTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      return {
        table: {
          widths: ["34%", "33%", "33%"],
          body: [
            [
              {
                text: isKn
                  ? `ರೋಗಿಯ ಹೆಸರು : ${name}`
                  : `Patient Name : ${name}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {
                text: isKn
                  ? `ವಯಸ್ಸು : ${field(patientInfo?.age)}`
                  : `Age : ${field(patientInfo?.age)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {
                text: isKn
                  ? `ಲಿಂಗ : ${field(patientInfo?.gender)}`
                  : `Gender : ${field(patientInfo?.gender)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
            ],

            [
              {
                text: isKn
                  ? `ಯು ಹ್ ಚ್ ಐಡಿ : ${field(patientInfo?.id)}`
                  : `UHID : ${field(patientInfo?.id)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {
                text: isKn
                  ? `ಐ ಪಿ ನಂಬರ್ : ${field(patientInfo?.ipopnumber)}`
                  : `IP No : ${field(patientInfo?.ipopnumber)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {
                text: isKn
                  ? `ವಾರ್ಡ್ : ${field(patientInfo?.roomName)}`
                  : `Ward : ${field(patientInfo?.roomName)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
            ],

            [
              {
                text: isKn
                  ? `ದಿನಾಂಕ : ${field(regDate)}`
                  : `Date : ${field(regDate)}`,
                bold: true,
                fontSize: 10,
                font: kn,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {
                text: isKn
                  ? `ಚಿಕಿತ್ಸಿಸುವ ವೈದ್ಯರು : ${field(
                      patientInfo?.doctorName
                    )}`
                  : `Treating Doctor : ${field(
                      patientInfo?.doctorName
                    )}`,
                bold: true,
                fontSize: 10,
                font: kn,
                colSpan: 2,
                border: [true, true, true, true],
                margin: [2, 3, 2, 3],
              },
              {},
            ],
          ],
        },
        margin: [0, 0, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // GUARDIAN SECTION
    //
    // Heading is OUTSIDE the table.
    // Table contains ONLY guardian data.
    // --------------------------------------------------------- 
    const guardianTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const guardianName = patientInfo?.guardianName;
      const guardianRelation = patientInfo?.guardianRelation;
      const guardianSignature = patientInfo?.guardianSignature;
      const guardianSignedDate = patientInfo?.guardianSignedDate;
      const guardianSignedTime = patientInfo?.guardianSignedTime;

      if (!guardianName && !guardianRelation && !guardianSignature) {
        return null;
      }

      return {
        stack: [
          {
            text: isKn
              ? "ರೋಗಿಗೆ ಸಹಿ ಮಾಡಲಾಗದಿದ್ದಲ್ಲಿ ಅಥವಾ ಅಪ್ರಾಪ್ತನಾಗಿದ್ದಲ್ಲಿ"
              : "If the patient cannot sign or minor",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 4],
          },
          {
            text: isKn
              ? "ಸಂಗಾತಿ / ಪೋಷಕರ / ಸಂಬಂಧಿಕರ ಸಹಿ :"
              : "Spouse, parent or guardian :",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 4],
          },
          {
            text: isKn
              ? "ರೋಗಿಯ ಜೊತೆಗಿನ ಸಂಬಂಧ :"
              : "Relationship with patient :",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 6],
          },

          {
            table: {
              widths: ["8%", "27%", "25%", "20%", "20%"],

              body: [
                [
                  {
                    text: "",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಹೆಸರು" : "Name",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಸಂಬಂಧ" : "Relationship",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ದಿನಾಂಕ / ಸಮಯ" : "Date / Time",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                ],

                [
                  {
                    text: "1.",
                    fontSize: 10,
                    alignment: "center",
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },
                  {
                    text: field(guardianName),
                    fontSize: 10,
                    font: kn,
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },
                  {
                    text: field(guardianRelation),
                    fontSize: 10,
                    font: kn,
                    border: [true, true, true, true],
                    margin: [2, 8, 2, 8],
                  },
                  guardianSignature
                    ? {
                        image: guardianSignature,
                        width: 90,
                        height: 40,
                        alignment: "center",
                        border: [true, true, true, true],
                        margin: [2, 5, 2, 5],
                      }
                    : {
                        text: "—",
                        fontSize: 10,
                        alignment: "center",
                        border: [true, true, true, true],
                        margin: [2, 8, 2, 8],
                      },
                  {
                    text: `${guardianSignedDate || "—"}\n${
                      guardianSignedTime || "—"
                    }`,
                    fontSize: 9,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                    margin: [2, 6, 2, 6],
                  },
                ],
              ],
            },
          },
        ],

        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // WITNESS SECTION
    //
    // Heading is OUTSIDE the table.
    // Table contains ONLY witness data.
    // ---------------------------------------------------------
    const witnessTable = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const witnesses = Array.isArray(patientInfo?.witnesses)
        ? patientInfo.witnesses
        : [];

      if (witnesses.length === 0) {
        return null;
      }

      return {
        stack: [
          // Witness heading
          {
            text: isKn ? "ಸಾಕ್ಷಿ" : "Witness",
            bold: true,
            fontSize: 10,
            font: kn,
            margin: [0, 0, 0, 6],
          },

          // Witness table
          {
            table: {
              widths: ["8%", "27%", "27%", "19%", "19%"],

              body: [
                // Header row
                [
                  {
                    text: "",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಹೆಸರು" : "Name",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಸಂಬಂಧ" : "Relationship",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                  {
                    text: isKn ? "ದಿನಾಂಕ / ಸಮಯ" : "Date / Time",
                    bold: true,
                    fontSize: 10,
                    font: kn,
                    alignment: "center",
                    border: [true, true, true, true],
                  },
                ],

                // Witness rows
                ...witnesses.map((w, index) => {
                  const signedDate = w.signedAt
                    ? helperServices.formatDateOnly(w.signedAt, true)
                    : w.signedDate || "—";

                  const signedTime = w.signedAt
                    ? helperServices.formatTimeOnly(w.signedAt)
                    : w.signedTime || "—";

                  return [
                    {
                      text: `${index + 1}.`,
                      fontSize: 10,
                      border: [true, true, true, true],
                      alignment: "center",
                      margin: [2, 8, 2, 8],
                    },

                    {
                      text: field(w.name),
                      fontSize: 10,
                      font: kn,
                      border: [true, true, true, true],
                      margin: [2, 8, 2, 8],
                    },

                    {
                      text: field(w.relation),
                      fontSize: 10,
                      font: kn,
                      border: [true, true, true, true],
                      margin: [2, 8, 2, 8],
                    },

                    w.signature
                      ? {
                          image: w.signature,
                          width: 80,
                          height: 35,
                          alignment: "center",
                          border: [true, true, true, true],
                          margin: [2, 5, 2, 5],
                        }
                      : {
                          text: "—",
                          fontSize: 10,
                          alignment: "center",
                          border: [true, true, true, true],
                          margin: [2, 8, 2, 8],
                        },

                    {
                      text: `${signedDate}\n${signedTime}`,
                      fontSize: 9,
                      font: kn,
                      alignment: "center",
                      border: [true, true, true, true],
                      margin: [2, 6, 2, 6],
                    },
                  ];
                }),
              ],
            },
          },
        ],

        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // REQUIRED GUARDIAN / WITNESS SECTION
    // ---------------------------------------------------------
    const requiredGuardianWitnessTable = (lang) => {
      const isKn = lang === "kn";
      const font = isKn ? "NotoSansKannada" : "Roboto";
      const hasGuardianData = Boolean(guardianTable(lang));
      const hasWitnessData = Boolean(witnessTable(lang));
      const participants = [];

      if (hasGuardianData) {
        participants.push({
          name: patientInfo?.guardianName,
          signature: patientInfo?.guardianSignature,
          relation: patientInfo?.guardianRelation,
        });
      }

      if (hasWitnessData) {
        participants.push(
          ...patientInfo.witnesses.map((witness) => ({
            name: witness?.name,
            signature: witness?.signature,
            relation: witness?.relation,
          })),
        );
      }

      while (participants.length < 2) {
        participants.push({ name: "", signature: null, relation: "" });
      }

      return {
        unbreakable: true,
        stack: [
          {
            text: isKn
              ? "ರೋಗಿಗೆ ಸಹಿ ಮಾಡಲಾಗದಿದ್ದಲ್ಲಿ ಅಥವಾ ಅಪ್ರಾಪ್ತನಾಗಿದ್ದಲ್ಲಿ"
              : "If the patient cannot sign or minor",
            bold: true,
            fontSize: 10,
            font,
            margin: [0, 0, 0, 4],
          },
          {
            text: isKn
              ? "ಸಂಗಾತಿ / ಪೋಷಕರ / ಸಂಬಂಧಿಕರ ಸಹಿ :"
              : "Spouse, parent or guardian :",
            bold: true,
            fontSize: 10,
            font,
            margin: [0, 0, 0, 4],
          },
          {
            text: isKn
              ? "ರೋಗಿಯ ಜೊತೆಗಿನ ಸಂಬಂಧ :"
              : "Relationship with patient :",
            bold: true,
            fontSize: 10,
            font,
            margin: [0, 0, 0, 6],
          },
          {
            table: {
              widths: ["7%", "33%", "27%", "33%"],
              body: [
                [
                  { text: "", border: [true, true, true, true] },
                  {
                    text: isKn ? "ಹೆಸರು" : "Name",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                  {
                    text: isKn ? "ಸಹಿ" : "Signature",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                  {
                    text: isKn
                      ? "ರೋಗಿಯ ಜೊತೆಗಿನ ಸಂಬಂಧ"
                      : "Relation with patient",
                    bold: true,
                    fontSize: 10,
                    font,
                    alignment: "center",
                  },
                ],
                ...participants.map((participant, index) => [
                  {
                    text: `${index + 1}.`,
                    fontSize: 10,
                    alignment: "center",
                    margin: [2, 9, 2, 9],
                  },
                  {
                    text: participant.name || "",
                    fontSize: 10,
                    font,
                    margin: [2, 9, 2, 9],
                  },
                  participant.signature
                    ? {
                        image: participant.signature,
                        fit: [90, 35],
                        alignment: "center",
                        margin: [2, 4, 2, 4],
                      }
                    : { text: "", margin: [2, 9, 2, 9] },
                  {
                    text: participant.relation || "",
                    fontSize: 10,
                    font,
                    margin: [2, 9, 2, 9],
                  },
                ]),
              ],
            },
          },
        ],
        margin: [0, 4, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // SIGNATURE BLOCK
    // ---------------------------------------------------------
    const sigBlock = (lang) => {
      const isKn = lang === "kn";
      const kn = isKn ? "NotoSansKannada" : "Roboto";

      const patientSignature = patientInfo?.patientSignature;
      const doctorSignature = patientInfo?.doctorSignature;

      return {
        columns: [
          {
            width: "50%",
            stack: [
              {
                text: isKn
                  ? "ರೋಗಿಯ ಸಹಿ :"
                  : "Patient Signature :",
                bold: true,
                fontSize: 10,
                font: kn,
              },

              patientSignature
                ? {
                    image: patientSignature,
                    width: 120,
                    height: 45,
                    margin: [0, 5, 0, 5],
                  }
                : {
                    text: " ",
                    fontSize: 10,
                    margin: [0, 12, 0, 0],
                  },

              {
                text: isKn
                  ? `ದಿನಾಂಕ : ${
                      patientInfo?.patientSignedDate || "—"
                    }`
                  : `Date : ${
                      patientInfo?.patientSignedDate || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              {
                text: isKn
                  ? `ಸಮಯ : ${
                      patientInfo?.patientSignedTime || "—"
                    }`
                  : `Time : ${
                      patientInfo?.patientSignedTime || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
                margin: [0, 4, 0, 0],
              },
            ],
          },

          {
            width: "50%",
            stack: [
              {
                text: isKn
                  ? `ವೈದ್ಯರ ಹೆಸರು ಮತ್ತು ಸಹಿ : ${field(
                      patientInfo?.doctorName
                    )}`
                  : `Name and Signature of Surgeon : ${field(
                      patientInfo?.doctorName
                    )}`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              doctorSignature
                ? {
                    image: doctorSignature,
                    width: 120,
                    height: 45,
                    margin: [0, 5, 0, 5],
                  }
                : {
                    text: " ",
                    fontSize: 10,
                    margin: [0, 12, 0, 0],
                  },

              {
                text: isKn
                  ? `ದಿನಾಂಕ : ${
                      patientInfo?.doctorSignedDate || "—"
                    }`
                  : `Date : ${
                      patientInfo?.doctorSignedDate || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
              },

              {
                text: isKn
                  ? `ಸಮಯ : ${
                      patientInfo?.doctorSignedTime || "—"
                    }`
                  : `Time : ${
                      patientInfo?.doctorSignedTime || "—"
                    }`,
                bold: true,
                fontSize: 10,
                font: kn,
                margin: [0, 4, 0, 0],
              },
            ],
          },
        ],
        margin: [0, 8, 0, 10],
      };
    };

    // ---------------------------------------------------------
    // DIVIDER
    // ---------------------------------------------------------
    const divider = {
      canvas: [
        {
          type: "line",
          x1: 0,
          y1: 0,
          x2: 515,
          y2: 0,
          lineWidth: 1,
          lineColor: "#000000",
        },
      ],
      margin: [0, 8, 0, 8],
    };

    // ---------------------------------------------------------
    // ENGLISH BODY
    // ---------------------------------------------------------
    const bodyEn = (diagnosisVal) => [
      {
        text: [
          "I  ",
          { text: name, bold: true },
          "  admitted for treatment under  Dr. ",
          {
            text: field(patientInfo?.doctorName),
            bold: true,
          },
          ", was explained about the nature of my illness and the proposed plan of treatment.",
        ],
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: `I understand that a diagnosis of ${diagnosisVal} has been made on me and that surgical operation has been advised by doctor.`,
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "The nature and purpose of the procedure, the risks of the procedure, the possibilities of complications have been explained to me, all my questions have been answered to my satisfaction.",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "It has been explained to me that a satisfactory result is expected but that the following are some of the complications or side effects that could or may occur, including but not limited to bleeding, infection, damage to adjacent tissues or organs, swelling, pain, suture reaction, recurrence, additional operations and in rare cases paralysis or death.",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "No guarantee has been given to me by anyone as to the results that may be obtained",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        ul: [
          "I consent to the doctor performing any additional procedures he deems necessary or advisable during the course of the procedure.",
          "I consent to the administration of such anaesthetics and drugs as may be considered necessary or advisable for this procedure.",
          "I understand that I am encouraged and invited to ask any further questions I may have. I understand all of my instructions and all of my questions have been answered to my satisfaction and with a clear mind, I have decided to agree for the surgical procedure offered to me and give my informed consent to undergo the above procedure.",
        ],
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },
    ];

    // ---------------------------------------------------------
    // KANNADA BODY
    // ---------------------------------------------------------
    const bodyKn = (diagnosisVal) => [
      {
        text: `ನಾನು ${name} ಡಾಕ್ಟರ್ ${field(
          patientInfo?.doctorName
        )} ನನ್ನ ಅನಾರೋಗ್ಯದ ಸ್ವರೂಪ ಮತ್ತು ಚಿಕಿತ್ಸೆಯ ಉದ್ದೇಶಿತ ಯೋಜನೆಯ ಬಗ್ಗೆ ವಿವರಿಸಲಾಗಿದೆ.`,
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: `ನನ್ನ ಮೇಲೆ ${diagnosisVal} ರೋಗನಿರ್ಣಯ ಮಾಡಲಾಗಿದೆ ಮತ್ತು ಶಸ್ತ್ರಚಿಕಿತ್ಸಾ ಕಾರ್ಯಾಚರಣೆಯನ್ನು ವೈದ್ಯರು ಸಲಹೆ ಮಾಡಿದ್ದಾರೆ ಎಂದು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ.`,
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "ಕಾರ್ಯವಿಧಾನದ ಸ್ವರೂಪ ಮತ್ತು ಉದ್ದೇಶ, ಕಾರ್ಯವಿಧಾನದ ಅಪಾಯಗಳು, ತೊಡಕುಗಳ ಸಾಧ್ಯತೆಗಳನ್ನು ನನಗೆ ವಿವರಿಸಲಾಗಿದೆ, ನನ್ನ ಎಲ್ಲಾ ಪ್ರಶ್ನೆಗಳಿಗೆ ನನ್ನ ತೃಪ್ತಿಗೆ ಉತ್ತರಿಸಲಾಗಿದೆ.",
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "ತೃಪ್ತಿದಾಯಕ ಫಲಿತಾಂಶವನ್ನು ನಿರೀಕ್ಷಿಸಲಾಗಿದೆ ಎಂದು ನನಗೆ ವಿವರಿಸಲಾಗಿದೆ ಆದರೆ ರಕ್ತಸ್ರಾವ, ಸೋಂಕು, ಪಕ್ಕದ ಅಂಗಾಂಶಗಳು ಅಥವಾ ಅಂಗಗಳಿಗೆ ಹಾನಿ, ಊತ, ನೋವು, ಸೇರಿದಂತೆ ಆದರೆ ಸೀಮಿತವಾಗಿರದ ಕೆಲವು ತೊಡಕುಗಳು ಅಥವಾ ಅಡ್ಡಪರಿಣಾಮಗಳು ಸಂಭವಿಸಬಹುದು. ಹೊಲಿಗೆಯ ಪ್ರತಿಕ್ರಿಯೆ, ಮರುಕಳಿಸುವಿಕೆ, ಹೆಚ್ಚುವರಿ ಕಾರ್ಯಾಚರಣೆಗಳು ಮತ್ತು ಅಪರೂಪದ ಸಂದರ್ಭಗಳಲ್ಲಿ ಪಾರ್ಶ್ವವಾಯು ಅಥವಾ ಸಾವು.",
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        text: "ಪಡೆಯಬಹುದಾದ ಫಲಿತಾಂಶಗಳ ಬಗ್ಗೆ ಯಾರೂ ನನಗೆ ಯಾವುದೇ ಗ್ಯಾರಂಟಿ ನೀಡಿಲ್ಲ",
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },

      {
        ul: [
          "ಕಾರ್ಯವಿಧಾನದ ಸಮಯದಲ್ಲಿ ವೈದ್ಯರು ಅಗತ್ಯ ಅಥವಾ ಸೂಕ್ತವೆಂದು ಭಾವಿಸುವ ಯಾವುದೇ ಹೆಚ್ಚುವರಿ ಕಾರ್ಯವಿಧಾನಗಳನ್ನು ನಿರ್ವಹಿಸಲು ನಾನು ಸಮ್ಮತಿಸುತ್ತೇನೆ.",
          "ಈ ಕಾರ್ಯವಿಧಾನಕ್ಕೆ ಅಗತ್ಯ ಅಥವಾ ಸಲಹೆ ನೀಡಬಹುದಾದಂತಹ ಅರಿವಳಿಕೆ ಮತ್ತು ಔಷಧಗಳ ಆಡಳಿತಕ್ಕೆ ನಾನು ಸಮ್ಮತಿಸುತ್ತೇನೆ.",
          "ನಾನು ಹೊಂದಿರುವ ಯಾವುದೇ ಹೆಚ್ಚಿನ ಪ್ರಶ್ನೆಗಳನ್ನು ಕೇಳಲು ನಾನು ಪ್ರೋತ್ಸಾಹಿಸಿದ್ದೇನೆ ಮತ್ತು ಆಹ್ವಾನಿಸಿದ್ದೇನೆ ಎಂದು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ. ನನ್ನ ಎಲ್ಲಾ ಸೂಚನೆಗಳನ್ನು ನಾನು ಅರ್ಥಮಾಡಿಕೊಂಡಿದ್ದೇನೆ ಮತ್ತು ನನ್ನ ಎಲ್ಲಾ ಪ್ರಶ್ನೆಗಳಿಗೆ ನನ್ನ ತೃಪ್ತಿ ಮತ್ತು ಸ್ಪಷ್ಟ ಮನಸ್ಸಿನಿಂದ ಉತ್ತರಿಸಲಾಗಿದೆ, ನನಗೆ ನೀಡಲಾದ ಶಸ್ತ್ರಚಿಕಿತ್ಸಾ ವಿಧಾನಕ್ಕೆ ನಾನು ಒಪ್ಪಿಕೊಳ್ಳಲು ನಿರ್ಧರಿಸಿದ್ದೇನೆ ಮತ್ತು ಮೇಲಿನ ಕಾರ್ಯವಿಧಾನಕ್ಕೆ ಒಳಗಾಗಲು ನನ್ನ ತಿಳುವಳಿಕೆಯುಳ್ಳ ಒಪ್ಪಿಗೆಯನ್ನು ನೀಡುತ್ತೇನೆ.",
        ],
        font: "NotoSansKannada",
        fontSize: 10,
        margin: [0, 0, 0, 8],
      },
    ];

    const diagnosisVal =
      patientInfo?.diagnosis ||
      "___________________________________";

    return {
      pageSize: "A4",
      pageMargins: [30, 30, 30, 30],

      defaultStyle: {
        font: "Roboto",
      },

      content: [
        // =====================================================
        // ENGLISH
        // =====================================================
        {
          text: "CONSENT FOR SURGERY / PROCEDURE",
          fontSize: 14,
          bold: true,
          alignment: "center",
          margin: [0, 0, 0, 10],
        },

        patientTable("en"),

        ...bodyEn(diagnosisVal),

        sigBlock("en"),

        requiredGuardianWitnessTable("en"),

        divider,

        // =====================================================
        // KANNADA
        // =====================================================
        {
          text: "ಶಸ್ತ್ರಚಿಕಿತ್ಸೆಗೆ ಒಪ್ಪಿಗೆ",
          pageBreak: "before",
          fontSize: 14,
          bold: true,
          alignment: "center",
          font: "NotoSansKannada",
          margin: [0, 0, 0, 10],
        },

        patientTable("kn"),

        ...bodyKn(diagnosisVal),

        sigBlock("kn"),

        requiredGuardianWitnessTable("kn"),
      ],
    };
  };

  // ---------------------------------------------------------
  // PRINT
  // ---------------------------------------------------------
  const printConsent = () => {
    pdfMake.vfs = {
      ...pdfFonts.pdfMake.vfs,
      "NotoSansKannada-Regular.ttf": NotoSansKannada,
    };

    pdfMake.fonts = {
      Roboto: {
        normal: "Roboto-Regular.ttf",
        bold: "Roboto-Medium.ttf",
        italics: "Roboto-Italic.ttf",
        bolditalics: "Roboto-MediumItalic.ttf",
      },

      NotoSansKannada: {
        normal: "NotoSansKannada-Regular.ttf",
        bold: "NotoSansKannada-Regular.ttf",
      },
    };

    const docDefinition = buildDocDefinition();

    pdfMake.createPdf(docDefinition).getBlob((blob) => {
      const url = URL.createObjectURL(blob);

      window.open(
        `/Patient_Profile_Print?file=${encodeURIComponent(url)}`,
        "_blank"
      );
    });
  };

  return null;
}

export default PrintSurgeryConsent;
